import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, CircularProgress, IconButton, LinearProgress, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Modal from '@Components/Modal';
import SbButton from '@Components/Button';
import { mapAiInvoicesToShipments, processVendorInvoiceExtraction } from './services/vendorInvoiceAiService';

interface ImportVendorInvoiceAiModalProps {
  open: boolean;
  onClose: () => void;
  onExtracted: (shipments: any[], fileName: string, fileSize: number) => void;
  acceptedFileTypes?: 'pdf_and_images' | 'all';
}

export default function ImportVendorInvoiceAiModal({
  open,
  onClose,
  onExtracted,
  acceptedFileTypes = 'all',
}: ImportVendorInvoiceAiModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state on modal open/close
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setIsProcessing(false);
      setStatusMessage('');
      setUploadProgress(0);
      setErrorMessage(null);
      setIsDragOver(false);
    }
  }, [open]);

  const validateFileType = (file: File): { isValid: boolean; error?: string } => {
    if (acceptedFileTypes === 'pdf_and_images') {
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      const mimeType = file.type?.toLowerCase() || '';
      const validExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
      const validMimes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
        'image/pjpeg',
        'image/x-png',
      ];

      // Check extension
      if (!validExtensions.includes(ext)) {
        return {
          isValid: false,
          error: 'Invalid file type. Please upload a PDF or image file (.pdf, .jpg, .jpeg, .png, .webp).',
        };
      }

      // Check MIME type if present and not a generic binary octet-stream
      if (
        mimeType &&
        mimeType !== 'application/octet-stream' &&
        mimeType !== 'binary/octet-stream' &&
        !validMimes.includes(mimeType)
      ) {
        return {
          isValid: false,
          error: 'Invalid file format. Only PDF and image files (.pdf, .jpg, .jpeg, .png, .webp) are supported.',
        };
      }
    }

    return { isValid: true };
  };

  const handleFileSelect = (file: File) => {
    const validation = validateFileType(file);
    if (!validation.isValid) {
      setErrorMessage(validation.error || 'Invalid file type.');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setErrorMessage(null);
    setSelectedFile(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isProcessing) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isProcessing) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleExtract = async () => {
    if (!selectedFile) {
      setErrorMessage('Please select a vendor invoice document first.');
      return;
    }

    const validation = validateFileType(selectedFile);
    if (!validation.isValid) {
      setErrorMessage(validation.error || 'Invalid file type.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setUploadProgress(0);

    try {
      const response = await processVendorInvoiceExtraction(
        selectedFile,
        msg => setStatusMessage(msg),
        percent => setUploadProgress(percent),
      );

      if (response.error) {
        setErrorMessage(response.error || 'Failed to extract vendor invoice details.');
        setIsProcessing(false);
        return;
      }

      if (response.fields) {
        const shipments = mapAiInvoicesToShipments(response.fields);
        if (!shipments || shipments.length === 0) {
          setErrorMessage('No valid shipment records could be extracted from this invoice.');
          setIsProcessing(false);
          return;
        }

        onExtracted(shipments, selectedFile.name, selectedFile.size);
        onClose();
      } else {
        setErrorMessage('No invoice data returned by the extraction service.');
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.msg ||
        err?.response?.data?.errorDetails ||
        err?.message ||
        'Failed to process invoice document. Please try again.';
      setErrorMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsProcessing(false);
    }
  };

  const acceptAttribute =
    acceptedFileTypes === 'pdf_and_images'
      ? '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp'
      : '.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.csv,.xlsx,.xls';

  return (
    <Modal open={open} onClose={isProcessing ? undefined : onClose} maxWidth="md">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        accept={acceptAttribute}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" className="font-bold" sx={{ color: 'text.primary', lineHeight: 1.2 }}>
              Import with AI
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {acceptedFileTypes === 'pdf_and_images'
                ? 'Upload a vendor invoice document (PDF or Image) to extract shipments and charges'
                : 'Upload any vendor invoice document (PDF, Image, Excel, Word) to extract shipments and charges'}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={isProcessing} size="small" sx={{ color: 'text.secondary' }}>
          <HighlightOffIcon />
        </IconButton>
      </Box>

      {/* Error display */}
      {errorMessage && (
        <Box
          sx={(theme: any) => ({
            mb: 3,
            p: 2,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          })}
        >
          <ErrorOutlineIcon color="error" />
          <Typography color="error" variant="body2" className="font-medium">
            {errorMessage}
          </Typography>
        </Box>
      )}

      {/* Main Content Area */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !isProcessing && fileInputRef.current?.click()}
            sx={(theme: any) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              minHeight: '220px',
              border: '2px dashed',
              borderColor: isDragOver
                ? theme.palette.primary.main
                : selectedFile
                ? theme.palette.primary.main
                : alpha(theme.palette.primary.main, 0.3),
              borderRadius: '16px',
              backgroundColor: isDragOver
                ? alpha(theme.palette.primary.main, 0.08)
                : selectedFile
                ? alpha(theme.palette.success.main, 0.04)
                : alpha(theme.palette.primary.main, 0.02),
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease-in-out',
              opacity: isProcessing ? 0.7 : 1,
              '&:hover': {
                borderColor: isProcessing ? undefined : theme.palette.primary.main,
                backgroundColor: isProcessing ? undefined : alpha(theme.palette.primary.main, 0.05),
              },
            })}
          >
            {selectedFile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: '48px', mb: 1 }} />
                <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: 'text.primary' }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2 }}>
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB — {selectedFile.type || 'Document'}
                </Typography>
                {!isProcessing && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}
                  >
                    Click or drag to choose a different file
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <CloudUploadIcon color="primary" sx={{ fontSize: '52px', mb: 1.5 }} />
                <Typography variant="subtitle1" className="font-bold mb-1" sx={{ color: 'text.primary' }}>
                  Choose 1 Vendor Invoice to Import
                </Typography>
                <Typography variant="body2" className="text-center mb-1" sx={{ color: 'text.secondary' }}>
                  Drag & drop or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                  {acceptedFileTypes === 'pdf_and_images'
                    ? 'Supports PDF, JPG, JPEG, PNG, or WEBP documents'
                    : 'Supports PDF, Images, Excel, Word, or CSV documents'}
                </Typography>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Processing indicator */}
      {isProcessing && (
        <Paper
          elevation={0}
          sx={(theme: any) => ({
            mt: 3,
            p: 2.5,
            borderRadius: '12px',
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <CircularProgress size={24} color="primary" />
            <Typography variant="body2" className="font-semibold" sx={{ color: 'text.primary' }}>
              {statusMessage || 'Processing invoice document...'}
            </Typography>
          </Box>
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box sx={{ width: '100%', mt: 1 }}>
              <LinearProgress
                color="primary"
                variant="determinate"
                value={uploadProgress}
                sx={{ borderRadius: 4, height: 6 }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', textAlign: 'right', mt: 0.5 }}
              >
                {uploadProgress}% uploaded
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <SbButton variant="outlined" onClick={onClose} disabled={isProcessing} sx={{ borderRadius: '8px !important' }}>
          Cancel
        </SbButton>
        <SbButton
          variant="contained"
          color="primary"
          onClick={handleExtract}
          disabled={!selectedFile || isProcessing}
          startIcon={isProcessing ? undefined : <AutoAwesomeIcon />}
          sx={{
            borderRadius: '8px !important',
          }}
        >
          {isProcessing ? 'Extracting Details...' : 'Extract & Process'}
        </SbButton>
      </Box>
    </Modal>
  );
}
